import React, { useCallback, Component, Fragment } from 'react';
import { PropTypes } from 'prop-types';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import {
  getRecurringOrder,
  getMainLoading,
  getIsPreviewModalOpened,
  getPreviewChanges,
} from 'redux/modules/recurring-orders-edit/selectors';

import { closeRecurringOrderEditModal } from 'redux/modules/recurring-orders/effects';
import {
  initForm,
  closePreviewModal,
  getRecurringOrdersEditionPreview,
  getRecurringOrdersCancelationPreview,
  editRecurringOrder,
} from 'redux/modules/recurring-orders-edit/effects';

import { StyledLoading } from 'modules/Common/form/styled';
import { Modal } from '@ontruck/orc';

import RoChangesPreviewModal from 'modules/recurring-orders/ChangesPreviewModal';
import FormModalHeader from 'modules/recurring-orders/FormModalHeader';
import RepeatsOn from 'modules/recurring-orders/RepeatsOn';
import { ShipperOrderFormWrapper, ShipperOrderForm } from 'modules/shipper-order/Form';

import {
  ACTIVE,
  PRICE_INCREMENT_EXTRA,
  DISCOUNT_EXTRA,
  NEGOTIATED_EXTRA,
} from 'modules/shipper-order/Form/constants';
import { TenantExtras } from 'tenant/bussines';

import { Field } from '@ontruck/orc';

const t = {
  edit: 'Edit recurrent order',
  submit: 'Update all unassigned and future orders',
  cost_for_job: 'Cost for job',
};

const activeExtras = TenantExtras?.activeExtras || [
  DISCOUNT_EXTRA,
  PRICE_INCREMENT_EXTRA,
  NEGOTIATED_EXTRA,
];
const disabledExtras = [];
const disabledFields = ['shipper', 'address', 'internal_reference'];

const CostComponent = injectIntl(({ intl, currency, setFieldValue, values }) => {
  const { cost_for_job } = values || {};

  const currencySymbol = intl.messages.currencies[currency];

  const onChange = useCallback(
    (_, { value }) => {
      setFieldValue('cost_for_job', value);
    },
    [setFieldValue]
  );

  return (
    <div>
      <Field
        value={cost_for_job}
        label={t.cost_for_job}
        width="S"
        asideRightText={currencySymbol}
        onChange={onChange}
      />
    </div>
  );
});

const Edition = ({ formikProps, onClose, onCancel }) => {
  const { values = {} } = formikProps;

  return (
    <Fragment>
      <Modal
        onClose={onClose}
        show={true}
        size="XL"
        headerComponent={
          <FormModalHeader onCancelClick={onCancel} showCancelButton={values.status === ACTIVE} />
        }
        subHeaderComponent={null}
      >
        <RepeatsOn schedule={values.schedule} {...formikProps} />
        <ShipperOrderForm
          onClose={onClose}
          allowShipperEdit={false}
          allowAddNewStop={false}
          allowUserEdit={true}
          submitText={t.submit}
          disabledFields={disabledFields}
          activeExtras={activeExtras}
          disabledExtras={disabledExtras}
          extraComponent={CostComponent}
          showDriverAndVehicle={true}
          {...formikProps}
        />
      </Modal>
    </Fragment>
  );
};

Edition.propTypes = {
  formikProps: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

class RecurringOrderEditModal extends Component {
  constructor(props) {
    super(props);

    this.handleClose = this.handleClose.bind(this);
    this.handleClosePreviewModal = this.handleClosePreviewModal.bind(this);
    this.handleOpenEditionPreviewModal = this.handleOpenEditionPreviewModal.bind(this);
    this.handleOpenCancelPreviewModal = this.handleOpenCancelPreviewModal.bind(this);
    this.handleEditRecurringOrder = this.handleEditRecurringOrder.bind(this);
  }

  componentDidMount() {
    const { id, initForm } = this.props;

    initForm(id);
  }

  handleClose() {
    const { closeEditModal, id } = this.props;
    closeEditModal({ id });
  }

  handleClosePreviewModal() {
    const { closePreviewModal } = this.props;
    closePreviewModal();
  }

  handleOpenEditionPreviewModal(values) {
    const { getRecurringOrdersEditionPreview } = this.props;

    getRecurringOrdersEditionPreview(values);
  }

  handleOpenCancelPreviewModal(values) {
    const { getRecurringOrdersCancelationPreview } = this.props;

    getRecurringOrdersCancelationPreview(values);
  }

  handleEditRecurringOrder(values) {
    const { editRecurringOrder } = this.props;

    editRecurringOrder(values);
  }

  render() {
    const { recurringOrder, mainLoading, isPreviewModalOpened } = this.props;

    if (mainLoading || !recurringOrder) {
      return (
        <Modal onClose={this.handleClose} show={true} size="XL" headerComponent={t.edit}>
          <StyledLoading />
        </Modal>
      );
    }

    return (
      <ShipperOrderFormWrapper
        initialShipperOrder={recurringOrder}
        onSubmit={(values) => this.handleOpenEditionPreviewModal(values)}
      >
        {(formikProps) => (
          <Fragment>
            <Edition
              formikProps={formikProps}
              onClose={this.handleClose}
              onCancel={() => this.handleOpenCancelPreviewModal(formikProps.values)}
              {...this.props}
            />

            {isPreviewModalOpened && (
              <RoChangesPreviewModal
                onCancel={this.handleClosePreviewModal}
                onClose={this.handleClosePreviewModal}
                onClick={() => this.handleEditRecurringOrder(formikProps.values)}
              />
            )}
          </Fragment>
        )}
      </ShipperOrderFormWrapper>
    );
  }
}

RecurringOrderEditModal.propTypes = {
  id: PropTypes.string.isRequired,
  mainLoading: PropTypes.bool.isRequired,
  isPreviewModalOpened: PropTypes.bool.isRequired,
  recurringOrder: PropTypes.object,
  closeEditModal: PropTypes.func.isRequired,
  initForm: PropTypes.func.isRequired,
  previewChanges: PropTypes.array.isRequired,
  closePreviewModal: PropTypes.func.isRequired,
  getRecurringOrdersEditionPreview: PropTypes.func.isRequired,
  getRecurringOrdersCancelationPreview: PropTypes.func.isRequired,
  editRecurringOrder: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => ({
  recurringOrder: getRecurringOrder(state),
  mainLoading: getMainLoading(state),
  isPreviewModalOpened: getIsPreviewModalOpened(state),
  previewChanges: getPreviewChanges(state),
});

const mapDispatchToProps = {
  closeEditModal: closeRecurringOrderEditModal,
  initForm: initForm,
  closePreviewModal: closePreviewModal,
  getRecurringOrdersEditionPreview: getRecurringOrdersEditionPreview,
  getRecurringOrdersCancelationPreview: getRecurringOrdersCancelationPreview,
  editRecurringOrder: editRecurringOrder,
};

export default connect(mapStateToProps, mapDispatchToProps)(RecurringOrderEditModal);
